import { Attribute, Category, Item } from "../ez-quote.types";
import { LOCAL_STORAGE_KEY } from "../ez-quote.helpers";

interface BackPressState {
  currentStep: number;
  draftItem: any;
  attributes: Attribute[];
  currentItem: { category: Category | null; attributes: any; condition: any };
  goldCurrentAttributeIndex: number;
  selectedCategoryParent: Category | null;
  categoryHistory: Category[][];
  categories: Category[];
  goldParentCategory: Category | null;
  isOtpModalVisible: boolean;
  otpModalMode: "phone" | "email";
}

interface BackPressSetters {
  setCurrentStep: (v: number) => void;
  setItems: React.Dispatch<React.SetStateAction<Item[]>>;
  setCurrentItem: (v: any) => void;
  setGoldCurrentAttributeIndex: React.Dispatch<React.SetStateAction<number>>;
  setattributeError: (v: string | null) => void;
  setSelectedCategoryParent: (v: Category | null) => void;
  setCategories: (v: Category[]) => void;
  setCategoryHistory: (v: Category[][]) => void;
  setSelectedRootCategory: (v: Category | null) => void;
  setGoldParentCategory: (v: Category | null) => void;
  setSelectedGoldItemType: (v: any) => void;
  setError: (v: string | null) => void;
  fetchAttributes: (categoryId: number) => void;
  resetAndFetchCategories: () => void;
  resetForm: () => void;
  setIsOtpModalVisible: (v: boolean) => void;
  setOtpModalMode: (v: "phone" | "email") => void;
}

export function makeHandleBackPress(
  state: BackPressState,
  setters: BackPressSetters
): () => void {
  const {
    currentStep, draftItem, attributes, currentItem, goldCurrentAttributeIndex,
    selectedCategoryParent, categoryHistory, categories, goldParentCategory,
    isOtpModalVisible, otpModalMode,
  } = state;
  const {
    setCurrentStep, setItems, setCurrentItem, setGoldCurrentAttributeIndex,
    setattributeError, setSelectedCategoryParent, setCategories, setCategoryHistory,
    setSelectedRootCategory, setGoldParentCategory, setSelectedGoldItemType,
    setError, fetchAttributes, resetAndFetchCategories, resetForm,
    setIsOtpModalVisible, setOtpModalMode,
  } = setters;

  return () => {
    if (isOtpModalVisible && otpModalMode === "phone") {
      setIsOtpModalVisible(false);
      return;
    }
    if (currentStep === 6) {
      setCurrentStep(5);
    } else if (currentStep === 5) {
      if (draftItem && "id" in draftItem) {
        setItems((prev) => prev.filter((item) => item.id !== (draftItem as Item).id));
      }
      setCurrentItem(draftItem);
      const isGoldFlow = attributes.some((a) => a.displayType === "SLIDER");
      if (isGoldFlow) {
        setGoldCurrentAttributeIndex(attributes.length - 1);
        setCurrentStep(3);
      } else {
        setCurrentStep(4);
      }
    } else if (currentStep === 4) {
      setCurrentStep(3);
      // Don't reset selectedCondition when going back
      // Only fetch attributes if we don't have them already
      if (currentItem.category && (!attributes || attributes.length === 0)) {
        fetchAttributes(currentItem.category.category_id);
      }
    } else if (currentStep === 3 && attributes.some((a) => a.displayType === "SLIDER") && goldCurrentAttributeIndex > 0) {
      // Back within Gold attribute flow: go to previous attribute screen
      setGoldCurrentAttributeIndex((prev) => prev - 1);
      setattributeError(null);
      return;
    } else if (currentStep === 3) {
      // Back from attributes: go back to the immediate parent category

      // First, try to restore the proper state from localStorage if we're missing data
      if (!selectedCategoryParent && currentItem.category) {

        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (parsed.selectedCategoryParent) {

              setSelectedCategoryParent(parsed.selectedCategoryParent);
            }
            if (parsed.categories && parsed.categories.length > 0) {

              setCategories(parsed.categories);
            }
          } catch (e) {
            console.error('Failed to parse localStorage:', e);
          }
        }
      }

      if (currentItem.category && selectedCategoryParent) {
        // We have a selected parent category, go back to it

        // Ensure we have the child categories loaded
        if (selectedCategoryParent.childCategories && selectedCategoryParent.childCategories.length > 0) {
          setCategories(selectedCategoryParent.childCategories);
        }

        setCurrentStep(2);
        return;
      } else if (categoryHistory.length > 0) {
        // Fallback: if there is a parent category in history, restore it
        const newHistory = [...categoryHistory];
        const previousCategories = newHistory.pop();
        setCategoryHistory(newHistory);
        if (previousCategories) {
          setCategories(previousCategories);
          // If we're going back to the Electronics level, clear the selected parent
          if (newHistory.length === 0) {
            setSelectedCategoryParent(null);
            setSelectedRootCategory(null);
          } else {
            // Set the parent from the remaining history
            const remainingHistory = [...newHistory];
            const parentCategory = remainingHistory[remainingHistory.length - 1];
            if (parentCategory && parentCategory.length > 0) {
              // Find the parent category that contains the current categories
              const parent = parentCategory.find(cat =>
                cat.childCategories && cat.childCategories.some(child =>
                  child.category_id === categories[0]?.category_id
                )
              );
              setSelectedCategoryParent(parent || null);
            }
          }
          setCurrentStep(2);
          return;
        }
      } else if (categories.length > 0) {
        // If we have categories but no history, we might be on child categories
        // If we have categories and they might be child categories,
        // we need to find their parent from the current item's category
        if (currentItem.category) {
          // For now, just go back to step 2 with current categories
          setCurrentStep(2);
          return;
        }
      }

      if (currentItem.category) {
        // If we have a current item category, we need to determine what level we should be at
        // and fetch the appropriate categories
        if (selectedCategoryParent) {
          // We're going back to a parent category level
          if (selectedCategoryParent.childCategories && selectedCategoryParent.childCategories.length > 0) {
            setCategories(selectedCategoryParent.childCategories);
          } else {
            // Parent doesn't have child categories, fetch fresh
            resetAndFetchCategories();
          }
        } else {
          // No parent category, go back to top level (Electronics)
          resetAndFetchCategories();
        }
      } else {
        // No current item, go back to top level
        resetAndFetchCategories();
      }

      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Back from Gold narrowing step — return to root category list
      if (goldParentCategory) {
        setGoldParentCategory(null);
        setSelectedGoldItemType(null);
        return;
      }
      // Back in category selection: if there's a history, go to parent's list.
      if (categoryHistory.length > 0) {
        const newHistory = [...categoryHistory];
        const previousCategories = newHistory.pop();
        setCategoryHistory(newHistory);
        if (previousCategories) {
          setCategories(previousCategories);
          setCurrentStep(2);
          // Update the selected parent category
          if (newHistory.length === 0) {
            setSelectedCategoryParent(null);
            setSelectedRootCategory(null);
          } else {
            const remainingHistory = [...newHistory];
            const parentCategory = remainingHistory[remainingHistory.length - 1];
            if (parentCategory && parentCategory.length > 0) {
              const parent = parentCategory.find(cat =>
                cat.childCategories && cat.childCategories.some(child =>
                  child.category_id === previousCategories[0]?.category_id
                )
              );
              setSelectedCategoryParent(parent || null);
            }
          }
          return;
        }
      }
      // If no parent categories remain, reset the form entirely.
      resetForm();
    }
    setError(null);
    setattributeError(null);
  };
}
